import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Typography, List, Avatar, Tag, Button, Space, message, Spin, Grid } from 'antd';
import {
    UserOutlined,
    ShoppingCartOutlined,
    InboxOutlined,
    PlusOutlined,
    FileTextOutlined,
    LineChartOutlined,
    FileSearchOutlined,
    RocketOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import dashboardService from '../services/dashboardService';

const { Title, Text } = Typography;

const ShortcutItem = ({ title, icon, link, color, isMobile }) => (
    <Col xs={8} sm={12} lg={8}>
        <Link to={link}>
            <div
                className="shortcut-item"
                style={{
                    padding: isMobile ? '16px 8px' : '24px 12px',
                    textAlign: 'center',
                    borderRadius: 16,
                    background: '#ffffff',
                    border: '1px solid #f0f0f0',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.1)';
                    e.currentTarget.style.borderColor = color;
                    e.currentTarget.style.transform = 'translateY(-6px)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
                    e.currentTarget.style.borderColor = '#f0f0f0';
                    e.currentTarget.style.transform = 'translateY(0)';
                }}
            >
                <div style={{
                    width: isMobile ? 40 : 52,
                    height: isMobile ? 40 : 52,
                    borderRadius: 12,
                    background: `linear-gradient(135deg, ${color}, ${color}dd)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: isMobile ? 8 : 12,
                    boxShadow: `0 4px 12px ${color}44`,
                    color: '#fff',
                    fontSize: isMobile ? 18 : 24
                }}>
                    {icon}
                </div>
                <Text strong style={{
                    fontSize: isMobile ? '11px' : '14px',
                    color: '#262626',
                    lineHeight: 1.2,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                }}>
                    {title}
                </Text>
            </div>
        </Link>
    </Col>
);

const Dashboard = () => {
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({
        totalSales: 0,
        newOrdersCount: 0,
        pendingOrdersCount: 0
    });

    const user = JSON.parse(localStorage.getItem('user') || '{}');

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            const result = await dashboardService.getDashboardData();
            if (result.success) {
                setData(result.data);
            }
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            message.error('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    const stats = [
        {
            title: 'Current FY Total Sales',
            value: data.totalSales,
            icon: <ShoppingCartOutlined />,
            color: '#1890ff',
            bg: '#e6f7ff',
            isCurrency: true
        },
        {
            title: 'New Orders (30 Days)',
            value: data.newOrdersCount,
            icon: <PlusOutlined />,
            color: '#52c41a',
            bg: '#f6ffed'
        },
        {
            title: 'Pending Orders',
            value: data.pendingOrdersCount,
            icon: <InboxOutlined />,
            color: '#fa8c16',
            bg: '#fff7e6'
        },
    ];

    const shortcutsData = [
        { title: 'New Customer', icon: <UserOutlined />, link: '/customers', color: '#1890ff' },
        { title: 'New Order', icon: <ShoppingCartOutlined />, link: '/orders', color: '#52c41a' },
        { title: 'New Lead', icon: <PlusOutlined />, link: '/leads', color: '#faad14' },
        { title: 'Generate Offer', icon: <FileTextOutlined />, link: '/quotations', color: '#722ed1' },
        { title: 'MSEDCL Docs', icon: <FileSearchOutlined />, link: '/msedcl', color: '#eb2f96' },
        { title: 'Order Report', icon: <LineChartOutlined />, link: '/reports/orders', color: '#fa541c' },
    ];



    return (
        <div style={{ padding: isMobile ? '8px' : '16px' }}>


            <Spin spinning={loading}>
                <Row gutter={[isMobile ? 12 : 20, isMobile ? 12 : 20]}>
                    {stats.map((item, index) => (
                        <Col xs={index === 0 ? 24 : 12} sm={12} lg={8} key={index}>
                            <Card
                                bordered={false}
                                style={{
                                    borderRadius: 20,
                                    background: item.bg,
                                    boxShadow: '0 8px 24px rgba(0,0,0,0.04)',
                                    overflow: 'hidden',
                                    position: 'relative',
                                    height: '100%',
                                    border: `1px solid ${item.color}15`
                                }}
                                bodyStyle={{ padding: isMobile ? '16px' : '24px' }}
                            >
                                <div style={{ position: 'absolute', right: isMobile ? -5 : -10, top: isMobile ? -5 : -10, opacity: 0.15, fontSize: isMobile ? 48 : 64, color: item.color }}>
                                    {item.icon}
                                </div>
                                <div style={{ position: 'relative', zIndex: 1 }}>
                                    <Text type="secondary" style={{ fontSize: isMobile ? 11 : 13, textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.8, opacity: 0.8, color: '#595959' }}>
                                        {item.title}
                                    </Text>
                                    <div style={{ marginTop: isMobile ? 4 : 8, display: 'flex', alignItems: 'baseline', gap: 4 }}>
                                        {item.isCurrency && <span style={{ fontSize: isMobile ? 16 : 20, color: '#262626', fontWeight: 600 }}>₹</span>}
                                        <Text style={{ fontSize: isMobile ? 22 : 32, fontWeight: 800, color: '#262626', lineHeight: 1 }}>
                                            {item.isCurrency ? item.value.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : item.value}
                                        </Text>
                                    </div>
                                    <div style={{ marginTop: isMobile ? 8 : 12 }}>
                                        <Tag
                                            color={item.color}
                                            style={{
                                                borderRadius: 20,
                                                border: 'none',
                                                fontSize: 10,
                                                padding: '0 12px',
                                                fontWeight: 600,
                                                background: `${item.color}20`,
                                                color: item.color
                                            }}
                                        >
                                            Live Data
                                        </Tag>
                                    </div>
                                </div>
                            </Card>
                        </Col>
                    ))}
                </Row>
            </Spin>

            <Row gutter={[24, 24]} style={{ marginTop: isMobile ? 24 : 40 }}>
                <Col span={24}>
                    <Card
                        bordered={false}
                        style={{
                            borderRadius: 20,
                            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                            background: '#fff'
                        }}
                        bodyStyle={{ padding: isMobile ? '16px' : '32px' }}
                    >
                        <div style={{ marginBottom: isMobile ? 20 : 28 }}>
                            <Space size={12}>
                                <div style={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: 12,
                                    background: '#1890ff15',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#1890ff'
                                }}>
                                    <RocketOutlined style={{ fontSize: 20 }} />
                                </div>
                                <Title level={4} style={{ margin: 0, fontWeight: 700 }}>Quick Shortcuts</Title>
                            </Space>
                        </div>

                        <Row gutter={[isMobile ? 12 : 20, isMobile ? 12 : 20]}>
                            {shortcutsData.map((shortcut, idx) => (
                                <ShortcutItem
                                    key={idx}
                                    title={shortcut.title}
                                    icon={shortcut.icon}
                                    link={shortcut.link}
                                    color={shortcut.color}
                                    isMobile={isMobile}
                                />
                            ))}
                        </Row>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default Dashboard;
